import '../assets/stylesheets/global/animate.css'
import { Controller } from 'stimulus'
import { enter, leave } from 'el-transition'
import { attachDynamicEvent } from '~/common/helpers/dom'
import { lockScroll, unlockScroll } from '~/common/helpers/scroll_lock'
import { registerEscLayer, unregisterEscLayer } from '~/common/helpers/esc_stack'
import { focusInto } from '~/common/helpers/focus_trap'
import { shouldDismissOnContentContainerClick } from './modal_overlay_click'
import { legacyDialogAbove } from '~/common/extensions/bootstrap_modal_layering.mjs'
import { handleDataFocus } from '~/common/behaviors/form_focus'
import $ from 'jquery'

// Escape keydowns a Bootstrap/TinyMCE dialog stacked above us gets to handle itself (see #escLayerClose)
const declinedEscEvents = new WeakSet()

export default class extends Controller {
  static targets = ['backdrop', 'contentContainer', 'content']
  static values = {
    dismissable: Boolean,
    closeOnOverlayClick: Boolean,
    closeOnEsc: Boolean,
    autofocus: Boolean,
    autofocusSelector: String,
    portal: Boolean,
    formDialog: Boolean, // form: option — see ModalComponent "Form mode"
    focusTrap: { type: Boolean, default: true },
  }

  // Ground truth for the leave().then() guards below, independent of the `hidden`/`in` classes
  // those same transitions are mutating. Mirrors the 'in' class start_in already reads from the
  // server, so a modal that starts open doesn't look closed to a hide() called before any show().
  #open = false

  connect() {
    this.#open = this.element.classList.contains('in')
    this._boundHide = this.hide.bind(this)
    this._boundClose = this.close.bind(this)
    this._boundShow = this.show.bind(this)
    this._boundBeforeHide = this.beforeHide.bind(this)

    this.element.addEventListener('modal:hide', this._boundHide)
    this.element.addEventListener('modal:close', this._boundClose)
    this.element.addEventListener('modal:show', this._boundShow)
    this.element.addEventListener('modal:beforeHide', this._boundBeforeHide)

    // start_in renders the 'in' class straight from the server — show() (and so the Esc-stack
    // registration inside it) is never called for that initial state. Same defect class as the
    // start_open dropdown gap elsewhere in this PR. ENG-33816.
    if (this.element.classList.contains('in')) {
      if (this.focusTrapValue) {
        this.#registerLayer()
        // Form dialogs autofocus below. Others take focus as a whole, not a field, so a page that
        // opens with a dialog (booking, onboarding survey) doesn't pop the mobile keyboard.
        if (!this.formDialogValue) focusInto(this.contentTarget, { first: false })
      } else if (this.closeOnEscValue) {
        registerEscLayer('modal', this.#escLayerClose)
      }
      // With focus_trap off and closeOnEsc off it registers nothing: not an open layer, so
      // hasAnyOpenLayer() stays false and page shortcuts keep working (builder editor popups)
    }
    // start_in form dialogs render open, so show()'s scroll lock + focus never ran for them.
    if (this.formDialogValue && this.element.classList.contains('in')) {
      this.#lockScroll()
      this.#autofocus()
    }

    if (this.portalValue) {
      if (this.element.parentNode !== document.body) {
        this.originalParent = this.element.parentNode
        this.originalNextSibling = this.element.nextSibling
        document.body.appendChild(this.element)
      }
      if (this.originalParent) {
        this.#observeParentRemoval()
      }
    }

    this.element.modal = (cmd) => {
      if (cmd === 'show') {
        this.show()
      }
      if (cmd === 'hide') {
        this.hide()
      }
      if (cmd === 'close') {
        this.close()
      }
    }
  }

  disconnect() {
    this.element.removeEventListener('modal:hide', this._boundHide)
    this.element.removeEventListener('modal:close', this._boundClose)
    this.element.removeEventListener('modal:show', this._boundShow)
    this.element.removeEventListener('modal:beforeHide', this._boundBeforeHide)
    unregisterEscLayer('modal', this.#escLayerClose)

    this.#disconnectParentObserver()
    // Put a portaled modal back where it was rendered, even when that page was just swapped out:
    // Turbo clones the old page for its Back cache after this runs, so a removed modal would be
    // missing there and its row or calendar chip would open nothing after Back (ENG-37194).
    if (
      this.portalValue &&
      this.originalParent &&
      this.element.parentNode !== document.body
    ) {
      const sibling =
        this.originalNextSibling?.parentNode === this.originalParent
          ? this.originalNextSibling
          : null
      this.originalParent.insertBefore(this.element, sibling)
    }
    this.#unlockScroll()
  }

  onEscapeKey(e) {
    // This is apparently fired even when you use Chrome's auto-complete feature and click on a auto-complete entry.
    // My best guess is chrome fires a keydown event in that case, but without a code. Stimulus intentionally fires all keydown even if code is not present?
    // So let's explicitly check for code here even though we do attach this with keydow.esc!
    if (e.code !== 'Escape') return
    if (declinedEscEvents.has(e)) return

    e.preventDefault()

    if (
      this.closeOnEscValue &&
      !this.backdropTarget.classList.contains('hidden')
    ) {
      this.close()
    }
  }

  // Bookkeeping only, see esc_stack.js — the keydown.esc@window action above still owns
  // actually closing this modal. This just tells the shared Esc stack "a modal is open" (when
  // it's actually closeable via Escape) so lower-priority layers (lightbox, mobile sidebar)
  // know to defer to it. ENG-33816.
  //
  // Returning false tells esc_stack to leave the key alone: a Bootstrap modal or TinyMCE dialog
  // opened on top of this modal handles its own Escape, and closing us underneath it would be wrong.
  #escLayerClose = (event) => {
    if (legacyDialogAbove(document)) {
      if (event) declinedEscEvents.add(event)
      return false
    }
    this.close()
  }

  show() {
    // Ignore overlay mousedown for a beat after open. Opening is wired to
    // `click` (after mouseup), but a double-click's second mousedown lands on
    // the full-screen content container and would otherwise dismiss immediately
    // (ENG-31568: "New list" name prompt closes before you can type).
    this.ignoreOverlayDismissUntil = Date.now() + 500
    this.element.ariaHidden = false
    this.#open = true
    this.dispatch('shown')
    this.#registerLayer()
    this.#lockScroll()
    enter(this.backdropTarget)
    enter(this.contentContainerTarget).then(() => {
      // A hide() that started before this show() can still be mid-flight (see hide()'s
      // leave().then() below) and settle after this — don't let a stale open finish opening.
      if (!this.#open) return
      this.element.classList.add('in')
      this.#autofocus()
    })
  }

  // Registered even when Escape doesn't close this modal, for its focus trap (Escape passes such a
  // layer by). The media dialog registers its own 'modal' closer right after dispatching
  // modal:show, so as the latest one it still gets Escape.
  #registerLayer() {
    registerEscLayer('modal', this.#escLayerClose, {
      escape: this.closeOnEscValue,
      focusTrap: this.focusTrapValue ? this.contentTarget : null,
    })
  }

  #autofocus() {
    this.#autofocusField()
    // Keyboard focus belongs in the dialog even with nothing to autofocus. With autofocus off,
    // take the dialog itself rather than a field (no mobile keyboard popping up).
    if (this.focusTrapValue) {
      focusInto(this.contentTarget, { first: this.autofocusValue })
    }
  }

  #autofocusField() {
    if (!this.autofocusValue) return

    // Form dialogs honor the enclosing form's data-focus, like the legacy Bootstrap modal helper did
    const form =
      this.formDialogValue && this.element.closest('form[data-focus]')
    if (form && !this.autofocusSelectorValue) {
      handleDataFocus(form)
      return
    }
    const selector =
      this.autofocusSelectorValue || 'input:visible, textarea:visible'
    $(this.element).find(selector).first().focus()
  }

  close() {
    // User-initiated closes only (X, overlay, Esc, modal#close buttons) — programmatic hide() skips this
    if (this.dispatch('beforeClose', { cancelable: true }).defaultPrevented) {
      return
    }
    this.stopVideo()
    this.dispatch('closed')
    this.hide()
  }

  // Stop any Simplero video players inside the modal
  stopVideo() {
    this.element.querySelectorAll('.video-js').forEach((video) => {
      window.videojs?.getPlayer(video.id)?.pause()
    })
  }

  hide() {
    const event = this.dispatch('beforeHide')
    if (event.defaultPrevented) return
    this.dispatch('hidden')
    unregisterEscLayer('modal', this.#escLayerClose)
    this.element.ariaHidden = true
    this.#open = false
    // leave() only adds the `hidden` class once its ~300ms CSS transition settles, not when it
    // starts. A show() landing inside that window clears `hidden` synchronously, but these
    // still-pending leave() calls would otherwise put it right back — stranding the modal hidden
    // (and, below, stripping 'in'/unlocking scroll) even though show() reopened it. Re-clear
    // `hidden` per target, and skip the close side-effects, if the modal is open again by settle.
    leave(this.backdropTarget).then(() => {
      if (this.#open) this.backdropTarget.classList.remove('hidden')
    })
    leave(this.contentContainerTarget).then(() => {
      if (this.#open) {
        this.contentContainerTarget.classList.remove('hidden')
        return
      }
      this.element.classList.remove('in')
      this.#unlockScroll()
    })
  }

  beforeHide() {
    this.element.querySelector(':focus')?.blur()
  }

  contentContainerClicked(e) {
    if (
      !shouldDismissOnContentContainerClick(e, {
        dismissable: this.dismissableValue,
        closeOnOverlayClick: this.closeOnOverlayClickValue,
        ignoreOverlayDismissUntil: this.ignoreOverlayDismissUntil,
      })
    ) {
      return
    }

    this.close()
  }

  #observeParentRemoval() {
    this.parentRemovalObserver = new MutationObserver(() => {
      if (this.originalParent && !this.originalParent.isConnected) {
        this.element.remove()
        this.#disconnectParentObserver()
      }
    })
    this.parentRemovalObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  #disconnectParentObserver() {
    this.parentRemovalObserver?.disconnect()
    this.parentRemovalObserver = null
  }

  #lockScroll = () => {
    this.lockedScroll = true
    lockScroll()
  }

  #unlockScroll = () => {
    if (!this.lockedScroll) return

    this.lockedScroll = false
    unlockScroll()
  }
}

attachDynamicEvent(
  '[data-trigger-modal]',
  'click',
  function (e) {
    // A JS-only `link_to(..., '#')` opener must not also jump to the top of the page
    if (this.getAttribute('href') === '#') e.preventDefault()
    document
      .querySelector(this.dataset.triggerModal)
      .dispatchEvent(new CustomEvent('modal:show'))
  },
  { capture: true }
)
